import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import moment from 'moment';
import { DateUtilsStringDate } from './date-utils.model';

type MomentUnit = 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second' | 'millisecond';

export const DAYS_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const SHORT_DAYS_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const MONTHS_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const SHORT_MONTHS_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const isStringDateValid = (dateString: string, dateDisplayFormat = 'DD/MM/YYYY'): boolean => {
  return moment(dateString, dateDisplayFormat, true).isValid();
};

export const dateToString = (date: Date, dateDisplayFormat = 'DD/MM/YYYY'): string => {
  return moment(date).format(dateDisplayFormat);
};

export const stringToDate = (dateString: string, dateDisplayFormat = 'DD/MM/YYYY'): Date | undefined => {
  const momentDate = moment(dateString, dateDisplayFormat, true);

  return momentDate.isValid() ? momentDate.toDate() : undefined;
};

export const isBeforeOrSameDate = (
  date: Date | DateUtilsStringDate,
  referenceDate: Date | DateUtilsStringDate,
  diffReference: MomentUnit = 'day',
): boolean => {
  if (!(date instanceof Date)) {
    date = stringToDate(date.date, date.format) as Date;
  }

  if (!(referenceDate instanceof Date)) {
    referenceDate = stringToDate(referenceDate.date, referenceDate.format) as Date;
  }

  return moment(date).isSameOrBefore(moment(referenceDate), diffReference);
};

export const isAfterOrSameDate = (
  date: Date | DateUtilsStringDate,
  referenceDate: Date | DateUtilsStringDate,
  diffReference: MomentUnit = 'day',
): boolean => {
  if (!(date instanceof Date)) {
    date = stringToDate(date.date, date.format) as Date;
  }

  if (!(referenceDate instanceof Date)) {
    referenceDate = stringToDate(referenceDate.date, referenceDate.format) as Date;
  }

  return moment(date).isSameOrAfter(moment(referenceDate), diffReference);
};

export const isSameDate = (
  date: Date | DateUtilsStringDate,
  referenceDate: Date | DateUtilsStringDate,
  diffReference: MomentUnit = 'day',
): boolean => {
  if (!(date instanceof Date)) {
    date = stringToDate(date.date, date.format) as Date;
  }

  if (!(referenceDate instanceof Date)) {
    referenceDate = stringToDate(referenceDate.date, referenceDate.format) as Date;
  }

  return moment(date).isSame(moment(referenceDate), diffReference);
};

export const isDateWithinBoundaries = (date: Date, minDate?: Date, maxDate?: Date) => {
  const isBeforeOrSameDateVar = !maxDate || isBeforeOrSameDate(date, maxDate);
  const isAfterOrSameDateVar = !minDate || isAfterOrSameDate(date, minDate);
  return isBeforeOrSameDateVar && isAfterOrSameDateVar;
};

export function dateFormatValidator(dateFormat: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = control.value;
    const isValid = moment(value, dateFormat, true).isValid();

    return !isValid ? { invalidDateFormat: { value: control.value, format: dateFormat } } : null;
  };
}

export function dateBoundariesValidator(dateFormat = 'DD/MM/YYYY', minDate?: Date, maxDate?: Date): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = control.value;
    const date = moment(value, dateFormat, true).toDate();

    return !isDateWithinBoundaries(date, minDate, maxDate) ? { dateOutOfBounds: { value: control.value } } : null;
  };
}

export const datesDifferenceInDays = (baseDate: Date, destinationDate: Date): number =>
  moment(destinationDate).diff(moment(baseDate), 'days');

/*
Formats date on default display of PIMS such as
11/Sept/2025 or 11/Sept/2025  15:33:52 without the use of pipe if we are doing on the controller...
 */
export function formatDisplayDate(timestamp: string | Date, withTime = false) {
  const date = new Date(timestamp);

  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();

  let display = `${day}/${month}/${year}`;

  if (withTime) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    display += `  ${hours}:${minutes}:${seconds}`;
  }

  return display;
}
